/*!
 * SLUMBR. — theme.js
 * Moteur d'animations (scroll, survol, scènes épinglées), panier AJAX,
 * page produit. JavaScript natif, aucune dépendance.
 */
(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  const S = (window.Slumbr = window.Slumbr || {});
  const cfg = Object.assign(
    {
      animations: true,
      chameleon: true,
      cursor: false,
      magnetic: true,
      cartType: 'drawer',
      moneyFormat: '{{amount_with_comma_separator}} €',
      locale: 'fr-FR',
      routes: { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_url: '/cart', root_url: '/' },
      strings: {}
    },
    S.config || {}
  );
  S.config = cfg;
  const t = (key, fallback) => (cfg.strings && cfg.strings[key]) || fallback;

  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches || !cfg.animations;
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  root.classList.remove('no-js');
  root.classList.add('js');
  if (!cfg.animations) root.classList.add('no-anim');

  /* ---------------------------------------------------------------------
     Utilitaires
     --------------------------------------------------------------------- */
  const $ = (s, c = doc) => c.querySelector(s);
  const $$ = (s, c = doc) => Array.from(c.querySelectorAll(s));
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, k) => a + (b - a) * k;
  const smooth = (e0, e1, x) => {
    const k = clamp((x - e0) / (e1 - e0));
    return k * k * (3 - 2 * k);
  };
  const easeOut = (k) => 1 - Math.pow(1 - k, 3);
  const once = (el, key) => {
    const flag = '__slumbr_' + key;
    if (el[flag]) return false;
    el[flag] = true;
    return true;
  };
  const safe = (fn, label) => {
    try {
      return fn();
    } catch (err) {
      console.warn('[SLUMBR] ' + (label || 'module') + ' :', err);
    }
  };
  const rafThrottle = (fn) => {
    let queued = false;
    return () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fn();
      });
    };
  };
  const seeded = (seed) => () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let k = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    k = (k + Math.imul(k ^ (k >>> 7), 61 | k)) ^ k;
    return ((k ^ (k >>> 14)) >>> 0) / 4294967296;
  };
  const whenVisible = (el, cb, margin = '120px') => {
    const io = new IntersectionObserver(([en]) => cb(en.isIntersecting), { rootMargin: margin });
    io.observe(el);
    return io;
  };

  function formatMoney(cents, format) {
    const pattern = String(format || cfg.moneyFormat).replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const parts = (n / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return pattern.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
        case 'amount_with_period_and_space_separator': return fmt(value, 2, ' ', '.');
        default: return fmt(value, 2);
      }
    });
  }
  S.formatMoney = formatMoney;

  /* ---------------------------------------------------------------------
     Boucle d'animation unique + état du scroll
     --------------------------------------------------------------------- */
  const Ticker = {
    fns: new Set(),
    last: 0,
    running: false,
    add(fn) {
      this.fns.add(fn);
      if (!this.running) {
        this.running = true;
        this.last = performance.now();
        requestAnimationFrame(this.loop);
      }
    },
    remove(fn) {
      this.fns.delete(fn);
    },
    loop(now) {
      const dt = Math.min(64, now - Ticker.last) / 1000;
      Ticker.last = now;
      Ticker.fns.forEach((fn) => fn(now, dt));
      if (Ticker.fns.size) requestAnimationFrame(Ticker.loop);
      else Ticker.running = false;
    }
  };

  const Scroll = {
    y: window.scrollY,
    vh: window.innerHeight,
    vw: window.innerWidth,
    docH: root.scrollHeight,
    velocity: 0,
    dir: 1,
    update() {
      const y = window.scrollY;
      const d = y - this.y;
      this.y = y;
      this.velocity = lerp(this.velocity, d, 0.18);
      if (Math.abs(d) > 0.5) this.dir = d > 0 ? 1 : -1;
    }
  };
  S.Scroll = Scroll;

  /* Éléments pilotés par la progression du scroll.
     p = 0 quand le haut de l'élément touche la ligne « start » (fraction
     de la hauteur d'écran), p = 1 quand son bas touche la ligne « end ». */
  const Engine = {
    items: [],
    add(el, opts) {
      const item = Object.assign({ el, start: 1, end: 0, top: 0, height: 0, p: -1, offset: 0, near: false }, opts);
      this.items.push(item);
      this.measureItem(item);
      return item;
    },
    measureItem(it) {
      const r = it.el.getBoundingClientRect();
      it.top = r.top + Scroll.y - (it.offset || 0);
      it.height = r.height;
    },
    measure() {
      Scroll.vh = window.innerHeight;
      Scroll.vw = window.innerWidth;
      Scroll.docH = root.scrollHeight;
      this.items = this.items.filter((it) => it.el.isConnected);
      this.items.forEach((it) => this.measureItem(it));
      Chameleon.measure();
      this.items.forEach((it) => (it.p = -1));
    },
    tick() {
      const y = Scroll.y;
      const vh = Scroll.vh;
      for (let i = 0; i < this.items.length; i++) {
        const it = this.items[i];
        const startY = it.top - vh * it.start;
        const endY = it.top + it.height - vh * it.end;
        const p = clamp((y - startY) / (endY - startY || 1));
        const near = y + vh > it.top - 300 && y < it.top + it.height + 300;
        if (!near && !it.near) continue;
        it.near = near;
        if (p !== it.p) {
          it.p = p;
          it.update(p, it);
        }
      }
    }
  };
  S.Engine = Engine;

  /* ---------------------------------------------------------------------
     Mode caméléon : le fond de page prend la couleur de la section
     qui traverse le milieu de l'écran.
     --------------------------------------------------------------------- */
  const Chameleon = {
    on: false,
    sections: [],
    current: null,
    init() {
      if (!cfg.chameleon || reduce) return;
      this.on = true;
      root.classList.add('is-chameleon');
      this.collect();
      this.tick(true);
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-chameleon-ready')));
    },
    collect() {
      if (!this.on) return;
      this.sections = $$('main [data-scheme], [data-chameleon]').map((el) => ({ el, top: 0, height: 0 }));
      this.measure();
    },
    measure() {
      if (!this.on) return;
      this.sections.forEach((s) => {
        const r = s.el.getBoundingClientRect();
        s.top = r.top + Scroll.y;
        s.height = r.height;
      });
    },
    tick(force) {
      if (!this.on) return;
      const line = Scroll.y + Scroll.vh * 0.5;
      let found = null;
      for (let i = 0; i < this.sections.length; i++) {
        const s = this.sections[i];
        if (line >= s.top && line < s.top + s.height) found = s.el;
      }
      if (!found) return;
      const scheme = found.dataset.schemeLive || found.dataset.scheme;
      if (force === true || scheme !== this.current) {
        this.current = scheme;
        doc.body.dataset.scheme = scheme;
      }
    }
  };

  /* ---------------------------------------------------------------------
     Découpe du texte (mots masqués) + révélations
     --------------------------------------------------------------------- */
  const Split = {
    words(el) {
      if (!once(el, 'split')) return;
      const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      const nodes = [];
      let n;
      while ((n = walker.nextNode())) nodes.push(n);
      nodes.forEach((node) => {
        if (node.parentElement && node.parentElement.closest('svg, .inline-icon, .w')) return;
        const parts = node.nodeValue.split(/([ \t\n\r\f]+)/);
        if (!node.nodeValue.trim()) return;
        const frag = doc.createDocumentFragment();
        parts.forEach((part) => {
          if (!part) return;
          if (/^[ \t\n\r\f]+$/.test(part)) {
            frag.appendChild(doc.createTextNode(' '));
            return;
          }
          const w = doc.createElement('span');
          w.className = 'w';
          const inner = doc.createElement('span');
          inner.className = 'w__i';
          inner.textContent = part;
          w.appendChild(inner);
          frag.appendChild(w);
        });
        node.parentNode.replaceChild(frag, node);
      });
    },
    lines(el) {
      let line = -1;
      let last = null;
      let lw = 0;
      $$('.w', el).forEach((w) => {
        const top = w.offsetTop;
        if (last === null || Math.abs(top - last) > 3) {
          line++;
          last = top;
          lw = 0;
        }
        w.style.setProperty('--line', line);
        w.style.setProperty('--lw', lw++);
      });
    }
  };

  const Reveal = {
    io: null,
    init() {
      if (this.io !== null) return;
      if (reduce || !('IntersectionObserver' in window)) {
        this.io = false;
        return;
      }
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            this.show(en.target);
            this.io.unobserve(en.target);
          });
        },
        { rootMargin: '0px 0px -9% 0px', threshold: 0 }
      );
    },
    show(el) {
      el.classList.add('is-in');
      if (el.hasAttribute('data-draw')) el.classList.add('is-drawn');
    },
    observe(scope = doc) {
      $$('[data-stagger]', scope).forEach((group) => {
        $$('[data-reveal]', group).forEach((el, i) => el.style.setProperty('--i', i));
      });
      const targets = $$('[data-reveal], [data-split], [data-draw]', scope).filter((el) => !el.classList.contains('is-in'));
      if (!this.io || designMode) {
        targets.forEach((el) => this.show(el));
        return;
      }
      targets.forEach((el) => this.io.observe(el));
    }
  };

  /* Mots qui s'allument au fil du scroll */
  const WordFill = {
    init(el) {
      if (!once(el, 'wordfill')) return;
      Split.words(el);
      const items = $$('.w, .inline-icon', el);
      if (reduce) return;
      Engine.add(el, {
        start: 0.86,
        end: 0.62,
        update: (p) => {
          const head = p * (items.length + 3) - 1.5;
          items.forEach((item, i) => {
            const o = clamp(head - i);
            if (item.classList.contains('inline-icon')) item.classList.toggle('is-on', o > 0.4);
            else item.style.setProperty('--o', (0.16 + o * 0.84).toFixed(3));
          });
        }
      });
    }
  };

  /* ---------------------------------------------------------------------
     Modules d'animation
     --------------------------------------------------------------------- */
  const Stars = {
    init(el) {
      if (!once(el, 'stars')) return;
      const count = parseInt(el.dataset.stars, 10) || 20;
      const maxY = parseFloat(el.dataset.starsMaxY || '100');
      const rand = seeded(count * 7919 + (parseInt(el.dataset.seed, 10) || 3));
      const frag = doc.createDocumentFragment();
      for (let i = 0; i < count; i++) {
        const star = doc.createElement('i');
        star.className = 'star';
        star.style.cssText =
          'left:' + (rand() * 100).toFixed(2) + '%;top:' + (rand() * maxY).toFixed(2) + '%;' +
          '--s:' + (1.4 + rand() * 2.6).toFixed(1) + 'px;--t:' + (2 + rand() * 3.5).toFixed(2) + 's;' +
          '--dl:' + (-rand() * 6).toFixed(2) + 's';
        frag.appendChild(star);
      }
      el.classList.add('stars-field');
      el.appendChild(frag);
    }
  };

  const HeroScene = {
    init(scene) {
      if (!once(scene, 'hero')) return;
      const section = scene.closest('.hero') || scene.parentElement;
      if (!reduce) {
        Engine.add(section, { start: 0, end: 0, update: (p) => section.style.setProperty('--hp', p.toFixed(4)) });
      }
      if (!finePointer || reduce) return;
      let tx = 0;
      let ty = 0;
      let x = 0;
      let y = 0;
      section.addEventListener('pointermove', (e) => {
        const r = scene.getBoundingClientRect();
        tx = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1.2, 1.2);
        ty = clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1.2, 1.2);
      }, { passive: true });
      section.addEventListener('pointerleave', () => {
        tx = 0;
        ty = 0;
      });
      Ticker.add(() => {
        if (!scene.isConnected) return;
        const nx = lerp(x, tx, 0.06);
        const ny = lerp(y, ty, 0.06);
        if (Math.abs(nx - x) < 0.0004 && Math.abs(ny - y) < 0.0004) return;
        x = nx;
        y = ny;
        scene.style.setProperty('--mx', x.toFixed(4));
        scene.style.setProperty('--my', y.toFixed(4));
      });
    }
  };

  const Marquee = {
    list: [],
    init(el) {
      if (!once(el, 'marquee')) return;
      const track = $('.marquee__track', el);
      const group = $('.marquee__group', el);
      if (!track || !group) return;
      const m = {
        el,
        track,
        group,
        skew: $('.marquee__skew', el),
        speed: parseFloat(el.dataset.speed || '1'),
        dir: parseFloat(el.dataset.direction || '1'),
        followScroll: el.hasAttribute('data-scroll-dir'),
        x: 0,
        w: 0,
        mult: 1,
        hover: false,
        visible: true
      };
      if (el.hasAttribute('data-pause-hover')) {
        el.addEventListener('pointerenter', () => (m.hover = true));
        el.addEventListener('pointerleave', () => (m.hover = false));
      }
      whenVisible(el, (v) => (m.visible = v), '50px');
      this.fill(m);
      this.list.push(m);
      if (reduce) return;
      Ticker.add((now, dt) => this.tick(m, dt));
    },
    fill(m) {
      $$('.marquee__group[aria-hidden="true"]', m.track).forEach((c) => c.remove());
      m.w = m.group.getBoundingClientRect().width;
      if (!m.w) return;
      const need = Math.ceil(m.el.getBoundingClientRect().width / m.w) + 1;
      for (let i = 0; i < need; i++) {
        const clone = m.group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        $$('a, button', clone).forEach((a) => a.setAttribute('tabindex', '-1'));
        m.track.appendChild(clone);
      }
      if (m.dir < 0) m.x = -m.w;
    },
    tick(m, dt) {
      if (!m.visible || !m.w || !m.el.isConnected) return;
      m.mult = lerp(m.mult, m.hover ? 0 : 1, 0.06);
      const dir = m.followScroll ? m.dir * Scroll.dir : m.dir;
      const v = 46 * m.speed * m.mult + Math.abs(Scroll.velocity) * 0.9 * m.speed;
      m.x -= v * dt * dir;
      if (m.x <= -m.w) m.x += m.w;
      if (m.x > 0) m.x -= m.w;
      m.track.style.transform = 'translate3d(' + m.x.toFixed(2) + 'px,0,0)';
      if (m.skew) m.skew.style.setProperty('--skew', clamp(-Scroll.velocity * 0.18, -7, 7).toFixed(2) + 'deg');
    },
    refresh() {
      this.list = this.list.filter((m) => m.el.isConnected);
      this.list.forEach((m) => this.fill(m));
    }
  };

  const Counter = {
    init(el) {
      if (!once(el, 'count')) return;
      const target = parseFloat(String(el.dataset.count).replace(',', '.'));
      const decimals = parseInt(el.dataset.decimals || '0', 10);
      const nf = new Intl.NumberFormat(cfg.locale || 'fr-FR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      if (reduce || isNaN(target)) return;
      el.textContent = nf.format(0);
      const io = new IntersectionObserver(([en]) => {
        if (!en.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const dur = 1900;
        const step = (now) => {
          const k = clamp((now - t0) / dur);
          el.textContent = nf.format(target * (1 - Math.pow(1 - k, 4)));
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }, { threshold: 0.5 });
      io.observe(el);
    }
  };

  const Parallax = {
    init(el) {
      if (!once(el, 'parallax') || reduce) return;
      const speed = parseFloat(el.dataset.parallax) || 0.15;
      Engine.add(el, {
        start: 1,
        end: 0,
        update: (p, it) => {
          const offset = (p - 0.5) * (it.height + Scroll.vh) * speed;
          it.offset = offset;
          el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
        }
      });
    }
  };

  /* Poudre de cacao versée depuis la cuillère (canvas) : un flot de grains,
     de la poussière fine qui flotte, puis des grains qui se dissolvent dans le lait.
     fx.pour (débit), fx.cloud et fx.spread (nuage de cacao) viennent du module Ritual. */
  const Powder = {
    init(canvas, fx) {
      if (!once(canvas, 'powder')) return;
      const stage = canvas.parentElement;
      const lip = $('[data-lip]', stage);
      const ctx = canvas.getContext('2d');
      if (!lip || !ctx) return;
      /* 4 teintes de grains + 1 voile de poussière fine, plus claire et translucide */
      const tones = ['#4a2c1f', '#5f3b2b', '#7a523d', '#3a2118', 'rgba(150,108,84,.5)'];
      const DUST = 4;
      const falling = [];
      const floating = [];
      const puffs = [];
      const rnd = Math.random;
      let w = 0;
      let h = 0;
      let k = 1;
      let carry = 0;
      let lipX = 0;
      let lipY = 0;
      let lastCloud = -1;
      let visible = false;
      let drawn = false;
      let drawnStill = false;
      const resize = () => {
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        w = stage.clientWidth;
        h = stage.clientHeight;
        k = w / 400;
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        lastCloud = -1;
      };
      resize();
      if ('ResizeObserver' in window) new ResizeObserver(resize).observe(stage);
      else window.addEventListener('resize', resize);
      whenVisible(stage, (v) => (visible = v), '0px');
      const drop = (list, i) => {
        list[i] = list[list.length - 1];
        list.pop();
      };
      /* Mesures reprises du SVG de la tasse (illus-cocoa, repère 400 × 400) */
      const clipMilk = () => {
        ctx.beginPath();
        ctx.ellipse(192 * k, 177.5 * k, 77 * k, 26.5 * k, 0, 0, Math.PI * 2);
        ctx.clip();
      };
      const step = (now, dt) => {
        if (!canvas.isConnected) return Ticker.remove(step);
        if (!visible) return;
        const still = !fx.pour && !falling.length && !floating.length && !puffs.length;
        if (still && !fx.cloud) {
          if (drawn) {
            ctx.clearRect(0, 0, w, h);
            drawn = false;
          }
          return;
        }
        /* plus aucun grain et nuage immobile : l'image est déjà à jour */
        if (still && drawnStill && fx.cloud === lastCloud) return;
        lastCloud = fx.cloud;
        drawnStill = still;
        const cx = 192 * k;
        const cy = 185 * k;
        const ry = 26 * k;
        if (fx.pour > 0) {
          const s = stage.getBoundingClientRect();
          const r = lip.getBoundingClientRect();
          const lx = r.left - s.left;
          const ly = r.top - s.top;
          lipX = lx;
          lipY = ly;
          carry += fx.pour * 1800 * k * dt;
          while (carry >= 1) {
            carry -= 1;
            if (falling.length > 3200) continue;
            const kind = rnd();
            const dust = kind < 0.35;
            falling.push({
              x: lx + (rnd() - 0.5) * 7 * k,
              y: ly + (rnd() - 0.5) * 3 * k,
              vx: (-10 + (rnd() - 0.5) * (dust ? 30 : 14)) * k,
              vy: (14 + rnd() * 24) * k,
              r: (dust ? 0.3 + rnd() * 0.3 : kind > 0.92 ? 0.9 + rnd() * 0.6 : 0.4 + rnd() * 0.45) * k,
              c: dust ? DUST : (rnd() * DUST) | 0,
              d: (rnd() + rnd() - 1) * 0.7
            });
          }
        }
        for (let i = falling.length - 1; i >= 0; i--) {
          const g = falling[i];
          const grain = g.r / k;
          /* vitesse limite : la poussière fine tombe moins vite et flotte davantage */
          g.vy = Math.min((230 + 250 * grain) * k, g.vy + 1100 * k * dt);
          g.vx += ((rnd() - 0.5) * 140 * k * dt) / grain;
          g.vx *= 1 - Math.min(1, 2.6 * dt);
          g.x += g.vx * dt;
          g.y += g.vy * dt;
          const land = cy + g.d * ry * 0.6;
          if (g.y < land) continue;
          drop(falling, i);
          if (g.c === DUST) continue;
          if (floating.length < 1600) {
            floating.push({ x: g.x, y: land, vx: (rnd() - 0.5) * 20 * k, vy: (rnd() - 0.5) * 6 * k, r: g.r * 1.15, c: g.c, t: 0, life: 0.5 + rnd() * 1.4 });
          }
          if (rnd() < 0.05 && puffs.length < 120) {
            puffs.push({ x: g.x, y: land, vx: (rnd() - 0.5) * 26 * k, vy: -(8 + rnd() * 24) * k, r: (2 + rnd() * 3) * k, t: 0, life: 0.5 + rnd() * 0.6 });
          }
        }

        ctx.clearRect(0, 0, w, h);
        drawn = true;
        ctx.save();
        clipMilk();
        if (fx.cloud > 0) {
          const rad = (16 + 66 * fx.spread) * k;
          ctx.save();
          ctx.translate(lipX || cx, cy);
          ctx.scale(1, 0.35);
          const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, rad);
          grad.addColorStop(0, 'rgba(86,50,34,' + (0.9 * fx.cloud).toFixed(3) + ')');
          grad.addColorStop(0.6, 'rgba(112,72,50,' + (0.45 * fx.cloud).toFixed(3) + ')');
          grad.addColorStop(1, 'rgba(112,72,50,0)');
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(0, 0, rad, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
        /* hors versage, les grains se dissolvent trois fois plus vite (ils ne tachent pas la mousse) */
        const melt = fx.pour > 0 ? 1 : 3;
        for (let i = floating.length - 1; i >= 0; i--) {
          const g = floating[i];
          g.t += dt * melt;
          if (g.t >= g.life) {
            drop(floating, i);
            continue;
          }
          g.vx *= 1 - Math.min(1, 1.6 * dt);
          g.vy *= 1 - Math.min(1, 1.6 * dt);
          g.x += g.vx * dt;
          g.y += g.vy * dt;
        }
        /* grains posés : regroupés par teinte et par opacité (4 paliers) pour limiter les tracés */
        for (let level = 1; level <= 4; level++) {
          ctx.globalAlpha = level / 4;
          for (let c = 0; c < DUST; c++) {
            ctx.fillStyle = tones[c];
            ctx.beginPath();
            for (let i = 0; i < floating.length; i++) {
              const g = floating[i];
              if (g.c !== c || Math.ceil((1 - g.t / g.life) * 4) !== level) continue;
              ctx.moveTo(g.x + g.r, g.y);
              ctx.ellipse(g.x, g.y, g.r, g.r * 0.6, 0, 0, Math.PI * 2);
            }
            ctx.fill();
          }
        }
        ctx.restore();
        for (let i = puffs.length - 1; i >= 0; i--) {
          const g = puffs[i];
          g.t += dt;
          if (g.t >= g.life) {
            drop(puffs, i);
            continue;
          }
          g.vy *= 1 - Math.min(1, 1.4 * dt);
          g.x += g.vx * dt;
          g.y += g.vy * dt;
          g.r += 10 * k * dt;
          ctx.fillStyle = 'rgba(120,80,58,' + (0.16 * (1 - g.t / g.life)).toFixed(3) + ')';
          ctx.beginPath();
          ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
          ctx.fill();
        }
        /* cœur du flot : un voile continu qui s'évase en tombant, sous les grains */
        if (fx.pour > 0.02) {
          const a = fx.pour;
          const bx = lipX - 3 * k;
          const veil = ctx.createLinearGradient(0, lipY, 0, cy);
          veil.addColorStop(0, 'rgba(92,56,38,' + (0.6 * a).toFixed(3) + ')');
          veil.addColorStop(1, 'rgba(92,56,38,' + (0.16 * a).toFixed(3) + ')');
          ctx.fillStyle = veil;
          ctx.beginPath();
          ctx.moveTo(lipX - 1.5 * k * a, lipY);
          ctx.lineTo(lipX + 1.5 * k * a, lipY);
          ctx.lineTo(bx + 3.4 * k * a, cy);
          ctx.lineTo(bx - 3.4 * k * a, cy);
          ctx.closePath();
          ctx.fill();
        }
        /* un seul tracé par teinte : des milliers de grains restent légers à dessiner */
        for (let c = tones.length - 1; c >= 0; c--) {
          ctx.fillStyle = tones[c];
          ctx.beginPath();
          for (let i = 0; i < falling.length; i++) {
            const g = falling[i];
            if (g.c !== c) continue;
            ctx.moveTo(g.x + g.r, g.y);
            ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
          }
          ctx.fill();
        }
      };
      Ticker.add(step);
    }
  };

  /* Scène « Le rituel » : épinglée, passage du jour à la nuit */
  const Ritual = {
    init(el) {
      if (!once(el, 'ritual')) return;
      const steps = $$('.ritual-step', el);
      const segs = $$('.ritual__seg span', el);
      const count = steps.length || 3;
      const clock = $('[data-clock]', el);
      const wave = $('[data-wave]', el);
      const toMin = (s) => {
        const [h, m] = String(s).split(':').map(Number);
        return h * 60 + (m || 0);
      };
      const from = toMin(el.dataset.from || '21:00');
      const to = toMin(el.dataset.to || '23:00');
      steps.forEach((s) => $$('.ritual-step__title', s).forEach((title) => Split.words(title)));
      let current = -1;
      let progress = 0;
      let visible = false;

      const setStep = (idx) => {
        if (idx === current) return;
        current = idx;
        const past = idx < 0 ? count - 1 : idx;
        steps.forEach((s, i) => {
          s.classList.toggle('is-active', i === idx);
          s.classList.toggle('is-past', i < past);
          s.setAttribute('aria-hidden', i === idx ? 'false' : 'true');
        });
      };
      setStep(0);
      el.dataset.phase = 'pour';

      /* La nuit tombe entre l'avant-dernière et la dernière étape : pendant
         ce court « fondu », aucun texte n'est affiché (lisibilité). */
      const dusk = (count - 1) / count;
      const gap = 0.045;
      const fx = { pour: 0, cloud: 0, spread: 0 };
      Engine.add(el, {
        start: 0,
        end: 1,
        update: (p) => {
          progress = p;
          const night = smooth(dusk - gap, dusk + gap, p);
          const moon = smooth(dusk - gap, Math.min(1, dusk + 0.28), p);
          el.style.setProperty('--p', p.toFixed(4));
          el.style.setProperty('--night', night.toFixed(4));
          el.style.setProperty('--moon', moon.toFixed(4));
          /* Tasse : la cuillère entre, s'incline et se vide ; le lait devient chocolat,
             tourbillon, mousse, puis une lune de cacao saupoudrée */
          const spoonIn = smooth(0.005, 0.06, p);
          const spoonOut = smooth(0.265, 0.315, p);
          fx.pour = smooth(0.09, 0.12, p) * (1 - smooth(0.245, 0.27, p));
          fx.cloud = smooth(0.1, 0.15, p) * (1 - smooth(0.16, 0.3, p));
          fx.spread = smooth(0.1, 0.26, p);
          const cup = {
            '--spoon': spoonIn * (1 - spoonOut),
            '--away': 1 - spoonIn + spoonOut,
            '--tilt': (0.75 * smooth(0.045, 0.12, p) + 0.25 * smooth(0.12, 0.25, p)) * (1 - 0.8 * smooth(0.25, 0.3, p)),
            '--heap': 1 - smooth(0.1, 0.262, p),
            '--mix': smooth(0.13, 0.3, p),
            '--swirl': smooth(0.17, 0.24, p) * (1 - smooth(0.31, 0.37, p)),
            '--froth': smooth(0.27, 0.36, p),
            '--art': smooth(0.37, 0.45, p),
            '--steam': (0.4 + 0.6 * smooth(0.28, 0.38, p)) * (1 - 0.55 * night),
            '--glow': (0.55 + 0.45 * smooth(0.3, 0.42, p)) * (1 - 0.75 * night)
          };
          for (const name in cup) el.style.setProperty(name, cup[name].toFixed(4));
          const raw = Math.min(count - 1, Math.floor(p * count * 0.9999));
          const inDusk = count > 1 && p > dusk - gap && p < dusk + gap;
          setStep(inDusk ? -1 : raw);
          const phase = p >= dusk - gap ? 'night' : raw === 0 ? 'pour' : 'savor';
          if (el.dataset.phase !== phase) el.dataset.phase = phase;
          segs.forEach((sg, i) => sg.style.setProperty('--f', clamp(p * count - i).toFixed(3)));
          if (clock) {
            const m = Math.round((from + (to - from) * p) / 5) * 5;
            const label = String(Math.floor(m / 60) % 24).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
            if (clock.textContent !== label) clock.textContent = label;
          }
          const live = night > 0.5 ? el.dataset.schemeNight || 'night' : el.dataset.scheme;
          if (el.dataset.schemeLive !== live) {
            el.dataset.schemeLive = live;
            Chameleon.tick();
          }
        }
      });
      const powder = $('[data-powder]', el);
      if (powder && !reduce) Powder.init(powder, fx);

      if (wave && !reduce) {
        whenVisible(el, (v) => (visible = v), '0px');
        Ticker.add((now) => {
          if (!visible || !wave.isConnected) return;
          const amp = lerp(11, 2.5, progress);
          const freq = lerp(6, 1.3, progress);
          const speed = lerp(260, 1100, progress);
          let d = '';
          for (let x = 0; x <= 200; x += 4) {
            const env = Math.sin((x / 200) * Math.PI);
            const yy = 16 + Math.sin((x / 200) * Math.PI * 2 * freq + now / speed) * amp * env;
            d += (x ? 'L' : 'M') + x + ' ' + yy.toFixed(2);
          }
          wave.setAttribute('d', d);
        });
      }
    }
  };

  /* Panneaux d'ingrédients extensibles */
  const Panels = {
    init(wrap) {
      if (!once(wrap, 'panels')) return;
      const panels = $$('.ing-panel', wrap);
      let timer = null;
      const head = (p) => $('.ing-panel__head', p);
      const activate = (panel) => {
        panels.forEach((p) => {
          const on = p === panel;
          p.classList.toggle('is-active', on);
          const h = head(p);
          if (h) h.setAttribute('aria-expanded', on ? 'true' : 'false');
          if (on) p.classList.add('is-drawn');
        });
        setTimeout(() => Engine.measure(), 950);
      };
      wrap.__activate = activate;
      panels.forEach((panel, i) => {
        panel.addEventListener('click', () => activate(panel));
        panel.addEventListener('keydown', (e) => {
          const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
          if (!step) return;
          e.preventDefault();
          const target = panels[(i + step + panels.length) % panels.length];
          activate(target);
          const h = head(target);
          if (h) h.focus({ preventScroll: true });
        });
        if (finePointer) {
          panel.addEventListener('mouseenter', () => {
            if (!matchMedia('(min-width: 990px)').matches) return;
            clearTimeout(timer);
            timer = setTimeout(() => activate(panel), 90);
          });
          panel.addEventListener('mouseleave', () => clearTimeout(timer));
        }
      });
      const io = new IntersectionObserver(([en]) => {
        if (!en.isIntersecting) return;
        io.disconnect();
        activate(panels.find((p) => p.classList.contains('is-active')) || panels[0]);
      }, { threshold: 0.25 });
      io.observe(wrap);
    }
  };

  /* Loupe qui révèle la structure « moléculaire » de la fève */
  const Loupe = {
    init(stage) {
      if (!once(stage, 'loupe')) return;
      const read = $('.loupe__read', stage);
      let w = 0;
      let h = 0;
      const measure = () => {
        const b = stage.getBoundingClientRect();
        w = b.width;
        h = b.height;
        stage.style.setProperty('--lr', Math.round(clamp(w * 0.19, 64, 128)) + 'px');
      };
      measure();
      if ('ResizeObserver' in window) new ResizeObserver(measure).observe(stage);
      let x = 0.5;
      let y = 0.5;
      let tx = 0.62;
      let ty = 0.4;
      let user = false;
      let lastUser = -9999;
      let visible = false;
      let lastLabel = '';
      const move = (e) => {
        const b = stage.getBoundingClientRect();
        tx = clamp((e.clientX - b.left) / b.width, 0.06, 0.94);
        ty = clamp((e.clientY - b.top) / b.height, 0.06, 0.94);
        user = true;
        lastUser = performance.now();
      };
      stage.addEventListener('pointermove', move, { passive: true });
      stage.addEventListener('pointerdown', move, { passive: true });
      stage.addEventListener('pointerleave', () => (user = false));
      whenVisible(stage, (v) => (visible = v), '0px');
      const render = () => {
        stage.style.setProperty('--lx', (x * w).toFixed(1) + 'px');
        stage.style.setProperty('--ly', (y * h).toFixed(1) + 'px');
        const label = '×4 · ' + x.toFixed(2) + ' / ' + y.toFixed(2);
        if (read && label !== lastLabel) {
          lastLabel = label;
          read.textContent = label;
        }
      };
      render();
      if (reduce) return;
      Ticker.add((now) => {
        if (!visible || !stage.isConnected) return;
        if (!user && now - lastUser > 1400) {
          const k = now / 1000;
          tx = 0.5 + Math.sin(k * 0.5) * 0.25;
          ty = 0.5 + Math.sin(k * 0.73 + 1.3) * 0.23;
        }
        x = lerp(x, tx, user ? 0.18 : 0.035);
        y = lerp(y, ty, user ? 0.18 : 0.035);
        render();
      });
    }
  };

  const Timeline = {
    init(el) {
      if (!once(el, 'timeline')) return;
      const ms = $$('.milestone', el);
      const n = ms.length || 1;
      if (reduce) {
        el.style.setProperty('--p', 1);
        ms.forEach((m) => m.classList.add('is-lit'));
        return;
      }
      Engine.add(el, {
        start: 0.78,
        end: 0.5,
        update: (p) => {
          el.style.setProperty('--p', p.toFixed(4));
          ms.forEach((m, i) => m.classList.toggle('is-lit', p > i / n - 0.001 && p > 0.001));
        }
      });
    }
  };

  const Rotator = {
    init(el) {
      if (!once(el, 'rotate') || reduce) return;
      let rot = 0;
      let visible = false;
      whenVisible(el, (v) => (visible = v));
      Ticker.add((now, dt) => {
        if (!visible || !el.isConnected) return;
        rot += dt * 9 + Scroll.velocity * 0.32;
        el.style.setProperty('--rot', rot.toFixed(2) + 'deg');
      });
    }
  };

  const Wordmark = {
    init(el) {
      if (!once(el, 'wordmark') || reduce) return;
      const chars = Array.from(el.children);
      const n = chars.length;
      const stagger = 0.07;
      Engine.add(el, {
        start: 1,
        end: 0.96,
        update: (p) => {
          chars.forEach((c, i) => {
            const local = clamp((p - i * stagger) / (1 - (n - 1) * stagger));
            c.style.setProperty('--ty', ((1 - easeOut(local)) * 100).toFixed(2) + '%');
          });
        }
      });
    }
  };

  const Accordion = {
    init(d) {
      if (!once(d, 'accordion')) return;
      const summary = $('summary', d);
      const content = summary && summary.nextElementSibling;
      if (!content) return;
      const finish = () => {
        delete d.dataset.animating;
        content.style.height = '';
        content.style.opacity = '';
        Engine.measure();
      };
      const close = (target, contentEl) => {
        target.dataset.animating = '1';
        const anim = contentEl.animate(
          [{ height: contentEl.offsetHeight + 'px', opacity: 1 }, { height: '0px', opacity: 0 }],
          { duration: 460, easing: 'cubic-bezier(.77,0,.175,1)' }
        );
        anim.onfinish = () => {
          target.open = false;
          delete target.dataset.animating;
          Engine.measure();
        };
      };
      summary.addEventListener('click', (e) => {
        if (reduce) return;
        e.preventDefault();
        if (d.dataset.animating) return;
        if (d.open) {
          close(d, content);
          return;
        }
        const group = d.dataset.group;
        if (group) {
          $$('details[data-group="' + group + '"][open]').forEach((other) => {
            if (other !== d) {
              const c = $('summary', other).nextElementSibling;
              if (c) close(other, c);
            }
          });
        }
        d.dataset.animating = '1';
        d.open = true;
        const h = content.scrollHeight;
        const anim = content.animate(
          [{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }],
          { duration: 620, easing: 'cubic-bezier(.16,1,.3,1)' }
        );
        anim.onfinish = finish;
      });
    }
  };

  const Announce = {
    init(el) {
      if (!once(el, 'announce')) return;
      const msgs = $$('.announce__msg', el);
      if (!msgs.length) return;
      msgs[0].classList.add('is-active');
      if (msgs.length < 2 || reduce) return;
      let i = 0;
      setInterval(() => {
        if (doc.hidden) return;
        const cur = msgs[i];
        i = (i + 1) % msgs.length;
        const next = msgs[i];
        cur.classList.remove('is-active');
        cur.classList.add('is-leaving');
        next.classList.remove('is-leaving');
        next.classList.add('is-active');
        setTimeout(() => cur.classList.remove('is-leaving'), 950);
      }, 4500);
    }
  };

  const StickyCta = {
    init(el) {
      if (!once(el, 'stickycta')) return;
      const hero = $('.hero, .product__info');
      const stoppers = $$('.cta-final, .footer, [data-sticky-stop]');
      let past = !hero;
      const vis = new Set();
      const update = () => {
        const show = past && vis.size === 0 && !doc.body.classList.contains('is-drawer-open');
        el.classList.toggle('is-visible', show);
      };
      if (hero) {
        new IntersectionObserver(([en]) => {
          past = !en.isIntersecting && en.boundingClientRect.top < 0;
          update();
        }).observe(hero);
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => (en.isIntersecting ? vis.add(en.target) : vis.delete(en.target)));
        update();
      });
      stoppers.forEach((s) => io.observe(s));
      doc.addEventListener('slumbr:drawer', update);
    }
  };

  /* Mini-avis qui défilent sous le titre produit */
  const Quotes = {
    init(el) {
      if (!once(el, 'quotes')) return;
      const items = $$('.quote-ticker__item', el);
      if (items.length < 2 || reduce) return;
      let i = 0;
      setInterval(() => {
        if (doc.hidden) return;
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 4200);
    }
  };

  /* ---------------------------------------------------------------------
     En-tête, menu mobile, barre de progression
     --------------------------------------------------------------------- */
  const Header = {
    init() {
      const header = $('[data-header]');
      if (!header) return;
      let lastY = Scroll.y;
      let hidden = false;
      let scrolled = null;
      Ticker.add(() => {
        const y = Scroll.y;
        const isScrolled = y > 8;
        if (isScrolled !== scrolled) {
          scrolled = isScrolled;
          header.classList.toggle('is-scrolled', isScrolled);
        }
        if (doc.body.classList.contains('is-menu-open')) return;
        if (Math.abs(y - lastY) > 6) {
          const hide = y > lastY && y > 360;
          if (hide !== hidden) {
            hidden = hide;
            header.classList.toggle('is-hidden', hide);
          }
          lastY = y;
        }
      });

      const toggle = $('[data-menu-toggle]');
      const menu = $('[data-mobile-menu]');
      if (!toggle || !menu) return;
      $$('li', menu).forEach((li, i) => li.style.setProperty('--i', i));
      const set = (open) => {
        menu.classList.toggle('is-open', open);
        menu.setAttribute('aria-hidden', open ? 'false' : 'true');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        doc.body.classList.toggle('is-menu-open', open);
        if (open) {
          header.classList.remove('is-hidden');
          hidden = false;
        }
      };
      toggle.addEventListener('click', () => set(!menu.classList.contains('is-open')));
      menu.addEventListener('click', (e) => {
        if (e.target.closest('a')) set(false);
      });
      doc.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) {
          set(false);
          toggle.focus();
        }
      });
    }
  };

  const ScrollProgress = {
    init() {
      const bar = $('[data-scroll-progress]');
      if (!bar) return;
      let last = -1;
      Ticker.add(() => {
        const max = Scroll.docH - Scroll.vh;
        const p = max > 0 ? clamp(Scroll.y / max) : 0;
        if (Math.abs(p - last) < 0.0005) return;
        last = p;
        bar.style.setProperty('--sp', p.toFixed(4));
      });
    }
  };

  /* ---------------------------------------------------------------------
     Survols : remplissage directionnel, aimant, inclinaison, curseur
     --------------------------------------------------------------------- */
  const Hover = {
    init() {
      if (!finePointer) return;
      const magnetic = cfg.magnetic && !reduce;
      if (magnetic) root.classList.add('has-magnetic');
      const setXY = (b, e) => {
        const r = b.getBoundingClientRect();
        b.style.setProperty('--x', (e.clientX - r.left).toFixed(1) + 'px');
        b.style.setProperty('--y', (e.clientY - r.top).toFixed(1) + 'px');
      };
      const resetTilt = (el) => {
        el.classList.remove('is-tilting');
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      };
      doc.addEventListener('pointerover', (e) => {
        const b = e.target.closest('.btn');
        if (b && !b.contains(e.relatedTarget)) setXY(b, e);
      });
      doc.addEventListener('pointerout', (e) => {
        const b = e.target.closest('.btn');
        if (b && !b.contains(e.relatedTarget)) setXY(b, e);
        const m = e.target.closest('[data-magnetic]');
        if (m && !m.contains(e.relatedTarget)) {
          m.style.setProperty('--mag-x', '0px');
          m.style.setProperty('--mag-y', '0px');
        }
        const tl = e.target.closest('[data-tilt]');
        if (tl && !tl.contains(e.relatedTarget)) resetTilt(tl);
      });
      doc.addEventListener('pointermove', (e) => {
        if (magnetic) {
          const m = e.target.closest('[data-magnetic]');
          if (m) {
            const r = m.getBoundingClientRect();
            const x = (e.clientX - r.left) / r.width - 0.5;
            const y = (e.clientY - r.top) / r.height - 0.5;
            m.style.setProperty('--mag-x', (x * 16).toFixed(2) + 'px');
            m.style.setProperty('--mag-y', (y * 14).toFixed(2) + 'px');
          }
        }
        if (!reduce) {
          const tl = e.target.closest('[data-tilt]');
          if (tl) {
            const r = tl.getBoundingClientRect();
            const px = (e.clientX - r.left) / r.width - 0.5;
            const py = (e.clientY - r.top) / r.height - 0.5;
            tl.classList.add('is-tilting');
            tl.style.setProperty('--rx', (-py * 7).toFixed(2) + 'deg');
            tl.style.setProperty('--ry', (px * 9).toFixed(2) + 'deg');
          }
        }
      }, { passive: true });
    }
  };

  /* Curseur : un simple point collé à la souris (aucun retard), qui grossit
     légèrement sur les éléments cliquables. */
  const Cursor = {
    init() {
      if (!finePointer || !cfg.cursor || reduce) return;
      root.classList.add('has-cursor');
      const dot = doc.createElement('div');
      dot.className = 'cursor';
      dot.setAttribute('aria-hidden', 'true');
      doc.body.appendChild(dot);
      let state = '';
      window.addEventListener('pointermove', (e) => {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        dot.style.transform = 'translate3d(' + e.clientX + 'px,' + e.clientY + 'px,0)';
        dot.classList.add('is-visible');
      }, { passive: true });
      doc.addEventListener('pointerover', (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;
        let next = '';
        if (target.closest('[data-cursor-none], input:not([type="radio"]):not([type="checkbox"]):not([type="submit"]), textarea, select, iframe, [contenteditable="true"]')) next = 'none';
        else if (target.closest('a, button, [role="button"], label, summary, .ing-panel:not(.is-active), [data-cursor-hover]')) next = 'hover';
        if (next === state) return;
        if (state) dot.classList.remove('is-' + state);
        if (next) dot.classList.add('is-' + next);
        state = next;
      });
      doc.addEventListener('pointerdown', () => dot.classList.add('is-down'));
      doc.addEventListener('pointerup', () => dot.classList.remove('is-down'));
      root.addEventListener('mouseleave', () => dot.classList.remove('is-visible'));
    }
  };

  /* ---------------------------------------------------------------------
     Panier AJAX + tiroir
     --------------------------------------------------------------------- */
  const Cart = {
    isOpen: false,
    lastFocus: null,
    timers: {},
    get drawer() {
      return $('[data-cart-drawer]');
    },
    init() {
      doc.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-toggle]');
        if (toggle && this.drawer && cfg.cartType === 'drawer') {
          e.preventDefault();
          this.isOpen ? this.close() : this.open(toggle);
          return;
        }
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          this.close();
          return;
        }
        const step = e.target.closest('[data-qty-change]');
        if (step && step.closest('[data-cart-drawer]')) {
          e.preventDefault();
          const line = step.closest('[data-line-key]');
          const out = $('[data-qty-value]', line);
          const qty = Math.max(0, (parseInt(out.textContent, 10) || 0) + parseInt(step.dataset.qtyChange, 10));
          /* « − » sur un pack seul : on redescend d'une cure (45 → 30 → 15 jours) au lieu de vider la ligne ; « Retirer » la supprime */
          if (qty === 0 && line.dataset.handle && !('plan' in line.dataset)) {
            this.stepDown(line);
            return;
          }
          out.textContent = qty;
          this.queueChange(line.dataset.lineKey, qty);
          return;
        }
        const remove = e.target.closest('[data-line-remove]');
        if (remove) {
          e.preventDefault();
          this.change(remove.dataset.lineRemove, 0);
          return;
        }
        const upgrade = e.target.closest('[data-gift-upgrade]');
        if (upgrade && upgrade.closest('[data-cart-drawer]')) {
          e.preventDefault();
          this.upgrade(upgrade);
        }
      });
      doc.addEventListener('submit', (e) => {
        const form = e.target.closest('form[data-product-form]');
        if (!form || cfg.cartType !== 'drawer' || !this.drawer) return;
        e.preventDefault();
        this.add(form);
      });
      doc.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') this.close();
        if (e.key === 'Tab') this.trap(e);
      });
      /* panier déjà rempli (visite précédente) : on regroupe aussi les sachets dès l'arrivée */
      const count = $('[data-cart-count]');
      if (count && (parseInt(count.textContent, 10) || 0) > 1) this.optimize();
    },
    open(trigger) {
      const drawer = this.drawer;
      if (!drawer) return;
      this.lastFocus = trigger || doc.activeElement;
      drawer.classList.remove('is-settled');
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      doc.body.classList.add('is-drawer-open');
      $$('[data-cart-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this.isOpen = true;
      const panel = $('.drawer__panel', drawer);
      setTimeout(() => panel && panel.focus({ preventScroll: true }), 60);
      setTimeout(() => drawer.classList.add('is-settled'), 1000);
      doc.dispatchEvent(new CustomEvent('slumbr:drawer', { detail: { open: true } }));
    },
    close() {
      const drawer = this.drawer;
      if (!drawer) return;
      drawer.classList.remove('is-open', 'is-settled');
      drawer.setAttribute('aria-hidden', 'true');
      doc.body.classList.remove('is-drawer-open');
      $$('[data-cart-toggle]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.isOpen = false;
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      doc.dispatchEvent(new CustomEvent('slumbr:drawer', { detail: { open: false } }));
    },
    trap(e) {
      const panel = $('.drawer__panel', this.drawer);
      const focusables = $$('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', panel).filter((n) => n.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && (doc.activeElement === first || doc.activeElement === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && doc.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    sectionId() {
      const d = this.drawer;
      return (d && d.dataset.sectionId) || 'cart-drawer';
    },
    async add(form) {
      const btn = $('[type="submit"]', form);
      const errorEl = $('[data-form-error]', form) || $('[data-form-error]', form.closest('[data-product]') || form);
      if (errorEl) errorEl.textContent = '';
      if (btn) {
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
      }
      const body = new FormData(form);
      body.append('sections', this.sectionId());
      body.append('sections_url', window.location.pathname);
      try {
        const res = await fetch(cfg.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body
        });
        const data = await res.json();
        if (!res.ok || data.status) throw data;
        this.render(data.sections);
        if (!this.isOpen) this.open(btn);
        this.bump();
        await this.optimize();
      } catch (err) {
        const msg = (err && (err.description || err.message)) || t('cartError', 'Une erreur est survenue. Réessayez.');
        if (errorEl) errorEl.textContent = msg;
        else window.alert(msg);
      } finally {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.removeAttribute('aria-busy');
        }
      }
    },
    async stepDown(line) {
      const packs = await this.packsFor(line.dataset.handle);
      const current = packs && packs.find((p) => String(p.id) === line.dataset.variantId);
      const smaller = current && packs.filter((p) => p.size < current.size).sort((a, b) => b.size - a.size)[0];
      if (!smaller) return this.change(line.dataset.lineKey, 0);
      line.classList.add('is-updating');
      try {
        const cart = await (await fetch(cfg.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })).json();
        const has = (cart.items || []).filter((i) => i.variant_id === smaller.id && !i.selling_plan_allocation).reduce((n, i) => n + i.quantity, 0);
        const res = await fetch((cfg.routes.cart_update_url || '/cart/update') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: { [current.id]: 0, [smaller.id]: has + 1 }, sections: this.sectionId(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw data;
        this.render(data.sections);
        await this.optimize();
      } catch (err) {
        line.classList.remove('is-updating');
        this.refresh();
      }
    },
    queueChange(key, qty) {
      clearTimeout(this.timers[key]);
      this.timers[key] = setTimeout(() => this.change(key, qty), 380);
    },
    async change(key, qty) {
      const drawer = this.drawer;
      const line = drawer && $$('[data-line-key]', drawer).find((l) => l.dataset.lineKey === key);
      if (line) line.classList.add('is-updating');
      try {
        const res = await fetch(cfg.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: qty, sections: this.sectionId(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw data;
        this.render(data.sections);
        await this.optimize();
      } catch (err) {
        if (line) line.classList.remove('is-updating');
        const box = drawer && $('[data-cart-error]', drawer);
        const msg = (err && (err.description || err.message)) || t('cartError', 'Une erreur est survenue. Réessayez.');
        if (box) {
          box.textContent = msg;
          box.hidden = false;
        }
      }
    },
    // carte cadeau du panier : remplace le sachet seul par le pack qui débloque l'e-book offert
    async upgrade(btn) {
      const drawer = this.drawer;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch((cfg.routes.cart_update_url || '/cart/update') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: { [btn.dataset.from]: 0, [btn.dataset.to]: 1 }, sections: this.sectionId(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw data;
        this.render(data.sections);
        this.bump();
        await this.optimize();
      } catch (err) {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        const box = drawer && $('[data-cart-error]', drawer);
        const msg = (err && (err.description || err.message)) || t('cartError', 'Une erreur est survenue. Réessayez.');
        if (box) {
          box.textContent = msg;
          box.hidden = false;
        }
      }
    },
    /* Prix des packs : 2 sachets ajoutés un par un coûteraient plus cher que le pack de 2 (69,80 € au lieu de 64,90 €).
       Après chaque changement du panier, les sachets d'un même produit (hors abonnement et hors personnalisation)
       sont regroupés dans la combinaison de packs la moins chère : le panier et le paiement affichent alors
       les prix de la fiche produit. Les packs sont les variantes dont le nom commence par un nombre (« 2 sachets · 30 doses »). */
    packs: {},
    packsFor(handle) {
      if (!this.packs[handle]) {
        this.packs[handle] = fetch((cfg.routes.root_url || '/').replace(/\/?$/, '/') + 'products/' + handle + '.js', { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((p) => {
            const list = ((p && p.variants) || [])
              .map((v) => ({ id: v.id, size: parseInt(v.title, 10), price: v.price, available: v.available }))
              .filter((v) => v.size >= 1 && v.available);
            return list.length > 1 && list.some((v) => v.size === 1) ? list : null;
          })
          .catch(() => null);
      }
      return this.packs[handle];
    },
    bestPacks(total, packs) {
      /* coût minimal pour exactement « total » sachets ; à coût égal, le moins de lignes possible */
      const best = [{ cost: 0, lines: 0, pick: null }];
      for (let n = 1; n <= total; n++) {
        best[n] = null;
        for (const p of packs) {
          const prev = best[n - p.size];
          if (n < p.size || !prev) continue;
          const cost = prev.cost + p.price;
          const lines = prev.lines + 1;
          if (!best[n] || cost < best[n].cost || (cost === best[n].cost && lines < best[n].lines)) best[n] = { cost, lines, pick: p };
        }
      }
      const counts = {};
      for (let n = total; n > 0 && best[n]; n -= best[n].pick.size) counts[best[n].pick.id] = (counts[best[n].pick.id] || 0) + 1;
      return best[total] ? counts : null;
    },
    async optimize() {
      if (this.optimizing) return;
      this.optimizing = true;
      try {
        const cart = await (await fetch(cfg.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })).json();
        const byProduct = {};
        for (const item of cart.items || []) {
          if (item.selling_plan_allocation || (item.properties && Object.keys(item.properties).length)) continue;
          (byProduct[item.handle] = byProduct[item.handle] || []).push(item);
        }
        const updates = {};
        for (const handle of Object.keys(byProduct)) {
          const packs = await this.packsFor(handle);
          if (!packs) continue;
          const items = byProduct[handle];
          const current = {};
          let total = 0;
          for (const item of items) {
            const p = packs.find((x) => x.id === item.variant_id);
            if (!p) continue;
            current[p.id] = (current[p.id] || 0) + item.quantity;
            total += p.size * item.quantity;
          }
          const target = total > 0 && this.bestPacks(total, packs);
          if (!target) continue;
          /* on ne touche au panier que si la combinaison de packs est vraiment moins chère */
          const cost = (counts) => packs.reduce((sum, p) => sum + p.price * (counts[p.id] || 0), 0);
          if (cost(target) < cost(current)) packs.forEach((p) => (updates[p.id] = target[p.id] || 0));
        }
        if (!Object.keys(updates).length) return;
        const res = await fetch((cfg.routes.cart_update_url || '/cart/update') + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: this.sectionId(), sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (res.ok && !data.status) this.render(data.sections);
      } catch (err) {
        /* en cas d'échec, le panier reste tel quel */
      } finally {
        this.optimizing = false;
      }
    },
    async refresh() {
      const res = await fetch(window.location.pathname + '?sections=' + this.sectionId());
      this.render(await res.json());
    },
    render(sections) {
      const drawer = this.drawer;
      if (!sections || !drawer) return this.refresh();
      const html = sections[this.sectionId()];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-inner]');
      const current = $('[data-cart-inner]', drawer);
      if (!fresh || !current) return;
      const oldFill = $('.ship-bar', current);
      const from = oldFill ? getComputedStyle(oldFill).getPropertyValue('--ship') : null;
      const newBar = $('.ship-bar', fresh);
      const to = newBar ? newBar.style.getPropertyValue('--ship') : null;
      if (newBar && from !== null && from !== '') newBar.style.setProperty('--ship', from.trim());
      current.replaceWith(fresh);
      if (newBar && to) requestAnimationFrame(() => requestAnimationFrame(() => newBar.style.setProperty('--ship', to)));
      const count = fresh.dataset.itemCount || '0';
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = false;
      });
    },
    bump() {
      $$('[data-cart-count]').forEach((el) => {
        el.classList.remove('is-bump');
        void el.offsetWidth;
        el.classList.add('is-bump');
      });
    }
  };
  S.Cart = Cart;

  /* ---------------------------------------------------------------------
     Page produit : variantes, abonnement, galerie, barre collante
     --------------------------------------------------------------------- */
  class Gallery {
    constructor(el) {
      this.el = el;
      this.slides = $$('.gallery__slide', el);
      this.thumbs = $$('[data-thumb]', el);
      this.dots = $$('.gallery__dots span', el);
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.thumbs.forEach((th, i) => th.addEventListener('click', () => this.go(i)));
      const vp = $('.gallery__viewport', el);
      if (!vp) return;
      let sx = null;
      vp.addEventListener('touchstart', (e) => (sx = e.touches[0].clientX), { passive: true });
      vp.addEventListener('touchend', (e) => {
        if (sx === null) return;
        const dx = e.changedTouches[0].clientX - sx;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1));
        sx = null;
      });
      vp.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.go(this.index + 1);
        if (e.key === 'ArrowLeft') this.go(this.index - 1);
      });
    }
    go(i) {
      if (typeof i !== 'number') {
        const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(i));
        if (idx < 0) return;
        i = idx;
      }
      const n = this.slides.length;
      if (!n) return;
      i = ((i % n) + n) % n;
      this.index = i;
      this.slides.forEach((s, k) => {
        s.classList.toggle('is-active', k === i);
      });
      this.thumbs.forEach((th, k) => th.setAttribute('aria-current', k === i ? 'true' : 'false'));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === i));
    }
  }

  class Product {
    constructor(el) {
      this.el = el;
      const json = $('[data-product-json]', el);
      this.data = json ? JSON.parse(json.textContent) : null;
      const units = $('[data-unit-prices]', el);
      this.unitPrices = units ? JSON.parse(units.textContent) : null;
      this.form = $('form[data-product-form]', el);
      const galleryEl = $('[data-gallery]', el);
      this.gallery = galleryEl ? new Gallery(galleryEl) : null;
      this.initQty();
      this.initSticky();
      this.initLinkedAdd();
      if (!this.data || !this.form) return;
      this.idInput = $('[name="id"]', this.form);
      this.planInput = $('[name="selling_plan"]', this.form);
      this.qtyInput = $('[name="quantity"]', this.form);
      el.addEventListener('change', (e) => {
        if (e.target.matches('[data-option-input], [data-purchase-input], [data-plan-select], [name="quantity"]')) this.update(e.target.matches('[data-option-input]'));
      });
      this.update(false);
    }
    initQty() {
      $$('[data-qty-step]', this.el).forEach((b) => {
        b.addEventListener('click', () => {
          const input = $('input', b.closest('.qty'));
          const v = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(b.dataset.qtyStep, 10));
          input.value = v;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
    initSticky() {
      const bar = $('[data-sticky-atc]');
      const mainBtn = $('[data-add-button]', this.el);
      if (!bar || !mainBtn) return;
      let out = false;
      let footer = false;
      const update = () => bar.classList.toggle('is-visible', out && !footer && !doc.body.classList.contains('is-drawer-open'));
      new IntersectionObserver(([en]) => {
        out = !en.isIntersecting && en.boundingClientRect.top < 0;
        update();
      }).observe(mainBtn);
      const foot = $('.footer');
      if (foot) {
        new IntersectionObserver(([en]) => {
          footer = en.isIntersecting;
          update();
        }).observe(foot);
      }
      doc.addEventListener('slumbr:drawer', update);
      const add = $('[data-sticky-add]', bar);
      if (add) {
        add.addEventListener('click', () => {
          if (!this.form) return;
          if (this.form.requestSubmit) this.form.requestSubmit();
          else this.form.submit();
        });
      }
    }
    /* boutons « Ajouter au panier » ailleurs sur la page (ex. section Bonne nuit) : ils ajoutent le pack choisi dans le bloc d'achat */
    initLinkedAdd() {
      $$('[data-main-add]').forEach((b) => {
        b.addEventListener('click', () => {
          if (!this.form) return;
          if (this.form.requestSubmit) this.form.requestSubmit();
          else this.form.submit();
        });
      });
    }
    /* « Cure 30 jours (2 sachets) · livraison et e-book offerts » d'après la carte du pack choisi */
    packNote(v) {
      const card = $$('[data-pack-card]', this.el).find((c) => c.dataset.variantId === String(v.id));
      if (!card) return '';
      const name = $('.pack-card__name', card);
      const meta = $('.pack-card__doses', card);
      const ship = $('[data-card-ship]', card);
      const gift = $('[data-card-gift]', card);
      const hasShip = !!(ship && !ship.hidden);
      const hasGift = !!(gift && !gift.hidden);
      let note = (name ? name.textContent.trim() : '') + (meta ? ' (' + meta.textContent.trim() + ')' : '');
      if (hasShip && hasGift) note += ' · ' + t('packBoth', 'livraison et e-book offerts');
      else if (hasShip) note += ' · ' + t('packShip', 'livraison offerte');
      else if (hasGift) note += ' · ' + t('packGift', 'e-book offert');
      return note;
    }
    selectedOptions() {
      return $$('[data-option-index]', this.el).map((fs) => {
        const c = $('input:checked', fs);
        return c ? c.value : null;
      });
    }
    variant() {
      const opts = this.selectedOptions();
      const variants = this.data.variants || [];
      if (!opts.length) return variants.find((v) => String(v.id) === String(this.idInput && this.idInput.value)) || variants[0];
      return variants.find((v) => v.options.every((o, i) => o === opts[i]));
    }
    planId() {
      const radio = $('[data-purchase-input]:checked', this.el);
      if (!radio || radio.value === 'once') return null;
      const select = $('[data-plan-select]', radio.closest('[data-purchase-option]'));
      return select ? select.value : radio.value;
    }
    /* Cartes des packs : prix par dose, économie et avantages selon l'abonnement ou l'achat unique */
    updatePacks(plan) {
      const wrap = $('[data-pack-cards]', this.el);
      if (!wrap) return;
      const d = wrap.dataset;
      const unitRef = Number(d.unitRef) || 0;
      const freeFrom = Number(d.freeFrom) || 0;
      const giftMin = Number(d.giftMin) || 0;
      $$('[data-pack-card]', wrap).forEach((card) => {
        const cv = (this.data.variants || []).find((x) => String(x.id) === card.dataset.variantId);
        if (!cv) return;
        const pack = Number(card.dataset.pack) || 1;
        const doses = Number(card.dataset.doses) || 0;
        const { price } = this.priceFor(cv, plan);
        const ref = Math.max(unitRef * pack, cv.compare_at_price || 0);
        const pct = ref > price ? Math.round((1 - price / ref) * 100) : 0;
        const badge = $('[data-card-badge]', card);
        if (badge) {
          badge.textContent = '−' + pct + ' %';
          badge.hidden = pct < 1;
        }
        const dose = $('[data-card-dose]', card);
        if (dose && doses) dose.textContent = formatMoney(Math.round(price / doses));
        const ship = $('[data-card-ship]', card);
        /* seuil de livraison offerte comparé au prix réellement payé (abonnement compris), comme au paiement */
        if (ship) ship.hidden = !((plan && d.subShip === 'true') || (freeFrom > 0 && price >= freeFrom));
        const gift = $('[data-card-gift]', card);
        if (gift) gift.hidden = !((plan && d.subGift === 'true') || (giftMin > 0 && pack >= giftMin));
      });
    }
    priceFor(v, plan) {
      if (plan && v.selling_plan_allocations) {
        const a = v.selling_plan_allocations.find((x) => String(x.selling_plan_id) === String(plan));
        if (a) return { price: a.price, compare: a.compare_at_price || v.price };
      }
      return { price: v.price, compare: v.compare_at_price };
    }
    update(fromOption) {
      const v = this.variant();
      const plan = this.planId();
      if (this.planInput) this.planInput.value = plan || '';
      const btn = $('[data-add-button]', this.el);
      const btnText = $('[data-add-text]', this.el);
      const sticky = $('[data-sticky-atc]');
      if (!v) {
        if (btn) btn.disabled = true;
        if (btnText) btnText.textContent = t('unavailable', 'Indisponible');
        return;
      }
      if (this.idInput) this.idInput.value = v.id;
      const { price, compare } = this.priceFor(v, plan);
      const qty = Math.max(1, parseInt(this.qtyInput && this.qtyInput.value, 10) || 1);
      $$('[data-price]', this.el).forEach((n) => (n.textContent = formatMoney(price)));
      $$('[data-compare]', this.el).forEach((n) => {
        n.textContent = compare > price ? formatMoney(compare) : '';
        n.hidden = !(compare > price);
      });
      $$('[data-add-price]', this.el).forEach((n) => (n.textContent = formatMoney(price * qty)));
      if (this.unitPrices) $$('[data-unit-price]', this.el).forEach((n) => (n.textContent = this.unitPrices[v.id] || ''));
      $$('[data-purchase-price]', this.el).forEach((n) => {
        const p = n.dataset.purchasePrice === 'once' ? this.priceFor(v, null) : this.priceFor(v, n.dataset.purchasePrice);
        n.textContent = formatMoney(p.price);
      });
      $$('[data-purchase-compare]', this.el).forEach((n) => {
        n.textContent = formatMoney(v.price);
      });
      this.updatePacks(plan);
      $$('[data-option-index]', this.el).forEach((fs) => {
        const c = $('input:checked', fs);
        const out = $('[data-selected-value]', fs);
        if (c && out) out.textContent = c.value;
      });
      if (btn) btn.disabled = !v.available;
      if (btnText) btnText.textContent = v.available ? t('addToCart', 'Ajouter au panier') : t('soldOut', 'Épuisé');
      if (sticky) {
        $$('[data-sticky-price]', sticky).forEach((n) => (n.textContent = formatMoney(price)));
        const sb = $('[data-sticky-add]', sticky);
        if (sb) sb.disabled = !v.available;
      }
      $$('[data-main-add]').forEach((b) => {
        b.disabled = !v.available;
        $$('[data-main-price]', b).forEach((n) => (n.textContent = formatMoney(price * qty)));
      });
      const note = this.packNote(v);
      if (note) $$('[data-main-note]').forEach((n) => (n.textContent = note));
      if (fromOption) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
        if (v.featured_media && this.gallery) this.gallery.go(String(v.featured_media.id));
      }
    }
  }

  /* ---------------------------------------------------------------------
     Initialisation
     --------------------------------------------------------------------- */
  function initScope(scope) {
    const run = (sel, fn, label) => $$(sel, scope).forEach((el) => safe(() => fn(el), label));
    run('[data-split]', (el) => Split.words(el), 'split');
    run('[data-word-fill]', (el) => WordFill.init(el), 'wordfill');
    run('[data-stars]', (el) => Stars.init(el), 'stars');
    run('[data-marquee]', (el) => Marquee.init(el), 'marquee');
    run('[data-count]', (el) => Counter.init(el), 'counter');
    run('[data-hero-scene]', (el) => HeroScene.init(el), 'hero');
    run('[data-ritual]', (el) => Ritual.init(el), 'ritual');
    run('[data-panels]', (el) => Panels.init(el), 'panels');
    run('[data-loupe]', (el) => Loupe.init(el), 'loupe');
    run('[data-timeline]', (el) => Timeline.init(el), 'timeline');
    run('[data-rotate]', (el) => Rotator.init(el), 'rotate');
    run('[data-wordmark]', (el) => Wordmark.init(el), 'wordmark');
    run('[data-parallax]', (el) => Parallax.init(el), 'parallax');
    run('details[data-accordion]', (el) => Accordion.init(el), 'accordion');
    run('[data-announce]', (el) => Announce.init(el), 'announce');
    run('[data-sticky-cta]', (el) => StickyCta.init(el), 'sticky-cta');
    run('[data-quotes]', (el) => Quotes.init(el), 'quotes');
    run('[data-product]', (el) => once(el, 'product') && new Product(el), 'product');
  }

  function revealScope(scope) {
    $$('[data-split]', scope).forEach((el) => safe(() => Split.lines(el), 'lines'));
    Reveal.init();
    Reveal.observe(scope);
    $$('.hero, [data-intro]', scope).forEach((h) => h.classList.add('is-loaded'));
  }

  function boot() {
    Ticker.add(() => {
      Scroll.update();
      Engine.tick();
      Chameleon.tick();
    });
    safe(() => Chameleon.init(), 'chameleon');
    initScope(doc);
    safe(() => Header.init(), 'header');
    safe(() => Cart.init(), 'cart');
    safe(() => Hover.init(), 'hover');
    safe(() => Cursor.init(), 'cursor');
    safe(() => ScrollProgress.init(), 'progress');

    const fonts = doc.fonts && doc.fonts.ready ? doc.fonts.ready : Promise.resolve();
    Promise.race([fonts, new Promise((r) => setTimeout(r, 650))]).then(() =>
      requestAnimationFrame(() => {
        safe(() => revealScope(doc), 'reveal');
        Engine.measure();
        S.ready = true;
      })
    );

    let lastW = window.innerWidth;
    let lastH = window.innerHeight;
    window.addEventListener('resize', rafThrottle(() => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w === lastW && Math.abs(h - lastH) < 120) return;
      lastW = w;
      lastH = h;
      $$('[data-split]').forEach((el) => Split.lines(el));
      Marquee.refresh();
      Engine.measure();
    }));
    if ('ResizeObserver' in window) new ResizeObserver(rafThrottle(() => Engine.measure())).observe(doc.body);
    window.addEventListener('load', () => {
      Engine.measure();
      Marquee.refresh();
    });

    /* Éditeur de thème Shopify */
    doc.addEventListener('shopify:section:load', (e) => {
      initScope(e.target);
      revealScope(e.target);
      $$('[data-reveal], [data-split], [data-draw]', e.target).forEach((n) => Reveal.show(n));
      Chameleon.collect();
      Engine.measure();
    });
    doc.addEventListener('shopify:section:unload', () =>
      setTimeout(() => {
        Chameleon.collect();
        Engine.measure();
      }, 0)
    );
    doc.addEventListener('shopify:section:select', (e) => {
      if ($('[data-cart-drawer]', e.target)) Cart.open();
    });
    doc.addEventListener('shopify:section:deselect', (e) => {
      if ($('[data-cart-drawer]', e.target)) Cart.close();
    });
    doc.addEventListener('shopify:block:select', (e) => {
      if (!e.target.closest) return;
      const panel = e.target.closest('.ing-panel');
      if (panel) {
        const wrap = panel.closest('[data-panels]');
        if (wrap && wrap.__activate) wrap.__activate(panel);
      }
      const msg = e.target.closest('.announce__msg');
      if (msg) $$('.announce__msg', msg.parentElement).forEach((m) => m.classList.toggle('is-active', m === msg));
      const faq = e.target.closest('details[data-accordion]');
      if (faq) faq.open = true;
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
